import { AuthBindings } from '@refinedev/core';
import { api } from '../services/api';

interface LoginResponse {
  session_token: string;
  user_id: string;
  expires_at: string;
}

export const authProvider: AuthBindings = {
  login: async ({ jwt }) => {
    try {
      // Call Control Plane /v1/auth/login endpoint
      const response = await api.post<LoginResponse>('/v1/auth/login', { jwt });

      const { session_token, user_id, expires_at } = response.data;

      // Store in localStorage
      localStorage.setItem('auth_token', session_token);
      localStorage.setItem('user_id', user_id);
      localStorage.setItem('expires_at', expires_at);

      return {
        success: true,
        redirectTo: '/',
      };
    } catch (error: unknown) {
      console.error('Login error:', error);

      // Type guard for axios error
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number; data?: { error?: { message?: string } } } };
        const errorMessage = axiosError.response?.data?.error?.message || 'Invalid JWT token';

        return {
          success: false,
          error: {
            name: 'LoginError',
            message: errorMessage,
          },
        };
      }

      return {
        success: false,
        error: {
          name: 'LoginError',
          message: 'Network error. Please check your connection.',
        },
      };
    }
  },

  logout: async () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('expires_at');

    return {
      success: true,
      redirectTo: '/login',
    };
  },

  check: async () => {
    const token = localStorage.getItem('auth_token');
    const expiresAt = localStorage.getItem('expires_at');

    if (!token || !expiresAt) {
      return {
        authenticated: false,
        redirectTo: '/login',
      };
    }

    // Check expiration
    const now = new Date();
    const expires = new Date(expiresAt);

    if (now >= expires) {
      return {
        authenticated: false,
        redirectTo: '/login',
        logout: true,
      };
    }

    return {
      authenticated: true,
    };
  },

  getPermissions: async () => {
    return null;
  },

  getIdentity: async () => {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      return null;
    }

    return {
      id: userId,
      name: userId,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(userId)}`,
    };
  },

  onError: async (error) => {
    if (error.status === 401 || error.statusCode === 401) {
      return {
        logout: true,
        redirectTo: '/login',
      };
    }

    return { error };
  },
};
